import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * The item list's multi-selection, owned by the PAGE.
 *
 * Two decisions in here are the whole hook, and both are about a feed that
 * moves under the user:
 *
 * 1. **Selection is keyed by item id, never by row index.** The feed
 *    re-sorts, re-filters and pages; `ItemCardsGrid` is re-mounted per group
 *    and a collapsed group's grid is unmounted entirely. A row index names a
 *    different item a second later, and "apply to rows 3, 7 and 11" would
 *    edit whatever had drifted into those positions.
 *
 * 2. **Any change to the query, the card filter or the sort drops the
 *    selection.** Those are exactly the changes that reset the feed's
 *    generation (`useCollectionItems`), so the ids in hand may no longer be
 *    in the list at all — and an apply to something the user can no longer
 *    see is the failure mode this guards. `allMatching` is worse still: it
 *    means "everything matching THAT filter", so it cannot survive the
 *    filter changing by so much as a character.
 *
 * `allMatching` is the by-filter selector's flag: the client does not hold
 * ids for it, the server resolves the set from the same query the list is
 * showing, and the count the user was looking at rides along as
 * `expectedCount` so a set that moved is a 409 rather than a silent apply to
 * a different set.
 */
export default function useItemSelection({ scopeToken, totalCount = 0 } = {}) {
	const [selectedIds, setSelectedIds] = useState(() => new Set());
	const [allMatching, setAllMatching] = useState(false);
	const [anchorId, setAnchorId] = useState(null);
	// Phones: the bar replaces the FAB while this is on, and a tile's
	// checkbox is always visible rather than hover-revealed.
	const [selectionMode, setSelectionMode] = useState(false);

	// The count the user was looking at when they said "all matching". Frozen
	// at that moment on purpose: it is the `expectedCount` the API compares
	// against, and re-reading a live total would defeat the check it exists
	// for.
	const [matchingCount, setMatchingCount] = useState(0);
	// Rows SUBTRACTED from an `allMatching` selection. The by-filter path
	// holds no ids, so this is the only way to say "everything matching that
	// filter except these three" — which all-or-nothing makes necessary the
	// first time the server refuses a row the client could not have known
	// about. It rides to the API as `select.exclude`; `expectedCount` stays
	// the filter's own total, so the 409 check still means what it says.
	const [excludedIds, setExcludedIds] = useState(() => new Set());

	const clear = useCallback(() => {
		setSelectedIds(new Set());
		setAllMatching(false);
		setExcludedIds(new Set());
		setAnchorId(null);
		setSelectionMode(false);
	}, []);

	// The feed's generation reset, mirrored. `useRef` rather than a plain
	// dependency so the FIRST render does not count as a change and clear a
	// selection nobody made yet.
	const lastScope = useRef(scopeToken);
	useEffect(() => {
		if (lastScope.current === scopeToken) return;
		lastScope.current = scopeToken;
		clear();
	}, [scopeToken, clear]);

	const isSelected = useCallback(id => selectedIds.has(id), [selectedIds]);

	const setIds = useCallback((next) => {
		setSelectedIds(next);
		// Any hand-picked change means the user is no longer saying "all
		// matching" — they are saying "these".
		setAllMatching(false);
	}, []);

	/** Turn one id on or off, and remember it as the range anchor. */
	const toggle = useCallback((id, on) => {
		if (!id) return;
		setSelectedIds((prev) => {
			const next = new Set(prev);
			const want = on === undefined ? !next.has(id) : Boolean(on);
			if (want) next.add(id); else next.delete(id);
			return next;
		});
		setAllMatching(false);
		setAnchorId(id);
	}, []);

	/**
	 * Shift-click / shift-arrow: everything from the anchor to `id` in the
	 * order the list is CURRENTLY showing, which is why the caller passes it
	 * — the hook must never assume a sort it cannot see.
	 *
	 * `canSelect` is the same question every OTHER path already asks — the
	 * table disables the checkbox, a tile's toggle returns early, "select all
	 * on this page" is handed only the selectable ids, and shift+arrow asks
	 * before it toggles. A range was the one gesture that did not, so it swept
	 * closed records and cards the user cannot write into the selection behind
	 * a checkbox that was visibly disabled — and the all-or-nothing apply was
	 * then refused by a row the UI had said could not be picked. The ORDER
	 * stays whole (a collapsed group is still not between the rows either side
	 * of it); only membership is filtered.
	 */
	const selectRange = useCallback((id, orderedIds, { canSelect } = {}) => {
		if (!id) return;
		const allowed = typeof canSelect === 'function' ? canSelect : () => true;
		const order = Array.isArray(orderedIds) ? orderedIds : [];
		const to = order.indexOf(id);
		const from = anchorId ? order.indexOf(anchorId) : -1;
		if (to < 0 || from < 0) {
			if (allowed(id)) toggle(id, true);
			return;
		}
		const [lo, hi] = from <= to ? [from, to] : [to, from];
		setSelectedIds((prev) => {
			const next = new Set(prev);
			for (let i = lo; i <= hi; i += 1) if (allowed(order[i])) next.add(order[i]);
			return next;
		});
		setAllMatching(false);
		// The anchor does NOT move on a range: dragging the range back and
		// forth from one anchor is the gesture people expect.
	}, [anchorId, toggle]);

	/**
	 * Replace the whole set. The table reports antd's own change this way —
	 * a click, a shift range, the header box — because antd's range fires no
	 * per-row callback at all and only the resulting key set is knowable.
	 * The anchor is left alone: the table sets it from the row the pointer
	 * was on.
	 */
	const replace = useCallback((ids) => {
		setSelectedIds(new Set((ids || []).filter(Boolean)));
		setAllMatching(false);
	}, []);

	/** The next range anchor, without changing what is selected. */
	const setAnchor = useCallback(id => setAnchorId(id || null), []);

	/** "Select all N on this page" — the ids the feed has actually loaded. */
	const selectVisible = useCallback((orderedIds) => {
		setIds(new Set((orderedIds || []).filter(Boolean)));
		setAnchorId(null);
	}, [setIds]);

	/**
	 * "Select all N matching this filter" — the by-filter selector. No ids
	 * are held: the count is, and the server resolves the set from the same
	 * query the list is showing.
	 */
	const selectAllMatching = useCallback(() => {
		setAllMatching(true);
		setMatchingCount(Number(totalCount) || 0);
		setExcludedIds(new Set());
		setAnchorId(null);
	}, [totalCount]);

	/**
	 * Drop ids from the selection — rows that are gone (deleted by the apply,
	 * or by somebody else) and rows the server refused.
	 *
	 * Under `allMatching` there is no `selectedIds` to filter — it is empty by
	 * construction — so this used to be a button that did nothing at all,
	 * leaving the user with a selection the server would refuse identically
	 * forever and no way forward but abandoning it. The by-filter selection is
	 * subtracted from instead, and the subtraction rides to the API as
	 * `select.exclude`.
	 */
	const forget = useCallback((ids) => {
		const drop = new Set((ids || []).filter(Boolean));
		if (!drop.size) return;
		setSelectedIds((prev) => {
			const next = new Set();
			prev.forEach(id => { if (!drop.has(id)) next.add(id); });
			return next;
		});
		if (!allMatching) return;
		setExcludedIds((prev) => {
			const next = new Set(prev);
			drop.forEach(id => next.add(id));
			return next;
		});
	}, [allMatching]);

	const ids = useMemo(() => Array.from(selectedIds), [selectedIds]);
	const excluded = useMemo(() => Array.from(excludedIds), [excludedIds]);

	return {
		selectedIds,
		ids,
		// What the by-filter selection has had subtracted from it. Empty
		// unless the user dropped rows the server refused.
		excluded,
		count: allMatching ? Math.max(matchingCount - excludedIds.size, 0) : selectedIds.size,
		// Is there a selection at all? `allMatching` with zero rows loaded is
		// still a selection.
		active: allMatching || selectedIds.size > 0,
		allMatching,
		matchingCount,
		anchorId,
		selectionMode,
		setSelectionMode,
		isSelected,
		toggle,
		replace,
		setAnchor,
		selectRange,
		selectVisible,
		selectAllMatching,
		forget,
		clear,
	};
}
